import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const betaSignUpSchema = z.object({
  betaCode: z.string().trim().min(3, "Code must be at least 3 characters").max(50),
  displayName: z.string().trim().min(2, "Name must be at least 2 characters").max(100),
  email: z.string().email("Invalid email address").max(255),
  password: z.string().min(8, "Password must be at least 8 characters").max(72),
  confirmPassword: z.string(),
}).refine(data => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type BetaSignUpFormValues = z.infer<typeof betaSignUpSchema>;

export function BetaSignUpForm() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const { toast } = useToast();

  const form = useForm<BetaSignUpFormValues>({
    resolver: zodResolver(betaSignUpSchema),
    defaultValues: {
      betaCode: "",
      displayName: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (values: BetaSignUpFormValues) => {
    setError("");
    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("beta-signup", {
        body: {
          betaCode: values.betaCode.toUpperCase().trim(),
          email: values.email,
          password: values.password,
          displayName: values.displayName,
        },
      });

      if (error) throw error;

      if (data?.error) {
        setError(data.error);
        return;
      }

      // If we got a magic link, use it to sign in automatically
      if (data?.magicLink) {
        const { error: signInError } = await supabase.auth.verifyOtp({
          token_hash: data.magicLink,
          type: "magiclink",
        });

        if (signInError) {
          console.error("Auto sign-in failed:", signInError);
          toast({
            title: "Account created!",
            description: "Please sign in with your credentials.",
          });
          return;
        }
      }

      toast({
        title: "Welcome!",
        description: "Your account has been created successfully.",
      });
    } catch (err: any) {
      console.error("Beta signup error:", err);
      setError(err.message || "Failed to create account. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="betaCode">Access Code</Label>
            <Input
              id="betaCode"
              {...form.register("betaCode")}
              className="uppercase"
              placeholder="XXXX-XXXX-XX"
            />
            {form.formState.errors.betaCode && (
              <p className="text-sm text-destructive">{form.formState.errors.betaCode.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="displayName">Display Name</Label>
            <Input
              id="displayName"
              placeholder="John Doe"
              {...form.register("displayName")}
            />
            {form.formState.errors.displayName && (
              <p className="text-sm text-destructive">{form.formState.errors.displayName.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="john@winery.com"
              {...form.register("email")}
            />
            {form.formState.errors.email && (
              <p className="text-sm text-destructive">{form.formState.errors.email.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              {...form.register("password")}
            />
            {form.formState.errors.password && (
              <p className="text-sm text-destructive">{form.formState.errors.password.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              {...form.register("confirmPassword")}
            />
            {form.formState.errors.confirmPassword && (
              <p className="text-sm text-destructive">{form.formState.errors.confirmPassword.message}</p>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating account...
              </>
            ) : (
              "Create Account"
            )}
          </Button>
        </form>
  );
}
